/**
 * Sender snapshot (PLAN.md 4.10): the author's profile at the moment of sending, carried in the
 * signed capsule as `["sender", <field>, <value>]` tags. Field names follow Nostr kind 0
 * metadata. Never email, login provider or account ids: a login is access, not identity.
 * The snapshot is the sender's own claim; apps show it together with the pubkey fingerprint.
 */
export type SenderSnapshot = { display_name: string; name?: string; about?: string };

export const SENDER_LIMITS = { display_name: 64, name: 32, about: 280 } as const;

export class ProfileError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ProfileError";
  }
}

const chars = (s: string) => [...s].length;
// Control characters (including line breaks) are not allowed in names; `about` may hold line breaks.
const CONTROL = /[\u0000-\u0008\u000b-\u001f\u007f-\u009f\u2028\u2029]/;
const NAME_CONTROL = /[\u0000-\u001f\u007f-\u009f\u2028\u2029]/;
const NICKNAME = /^[\p{L}\p{N}._-]+$/u;

/** Trims and checks a profile; throws ProfileError with a readable reason. Empty optional fields are dropped. */
export function normalizeProfile(input: unknown): SenderSnapshot {
  if (typeof input !== "object" || input === null) throw new ProfileError("profile must be an object");
  const p = input as Record<string, unknown>;
  const text = (v: unknown, field: string) => {
    if (v === undefined || v === null) return "";
    if (typeof v !== "string") throw new ProfileError(`${field} must be text`);
    return v.normalize("NFC").trim();
  };
  const display_name = text(p.display_name, "display name");
  const name = text(p.name, "nickname");
  const about = text(p.about, "about");
  if (!display_name) throw new ProfileError("display name is required");
  if (chars(display_name) > SENDER_LIMITS.display_name) throw new ProfileError(`display name is longer than ${SENDER_LIMITS.display_name} characters`);
  if (NAME_CONTROL.test(display_name)) throw new ProfileError("display name contains control characters");
  if (name && chars(name) > SENDER_LIMITS.name) throw new ProfileError(`nickname is longer than ${SENDER_LIMITS.name} characters`);
  if (name && !NICKNAME.test(name)) throw new ProfileError("nickname may contain letters, digits, dot, dash and underscore");
  if (about && chars(about) > SENDER_LIMITS.about) throw new ProfileError(`about is longer than ${SENDER_LIMITS.about} characters`);
  if (about && CONTROL.test(about)) throw new ProfileError("about contains control characters");
  return { display_name, ...(name ? { name } : {}), ...(about ? { about } : {}) };
}

/** Tags for a capsule. Validates first, so a capsule never carries a malformed snapshot. */
export function senderTags(snapshot: SenderSnapshot): string[][] {
  const s = normalizeProfile(snapshot);
  const tags = [["sender", "display_name", s.display_name]];
  if (s.name) tags.push(["sender", "name", s.name]);
  if (s.about) tags.push(["sender", "about", s.about]);
  return tags;
}

/**
 * The snapshot in a capsule's tags, or null when it has none (older capsules, other clients).
 * Unknown fields are ignored, so later fields do not break older readers. Throws ProfileError
 * for a malformed one: a repeated field, a missing display name, or values over the limits.
 */
export function readSender(event: { tags: string[][] }): SenderSnapshot | null {
  const tags = event.tags.filter((t) => t[0] === "sender");
  if (tags.length === 0) return null;
  const fields: Record<string, string> = {};
  for (const t of tags) {
    if (t.length !== 3) throw new ProfileError("a sender tag has the form [sender, field, value]");
    const [, field, value] = t;
    if (field !== "display_name" && field !== "name" && field !== "about") continue;
    if (field in fields) throw new ProfileError(`sender ${field} appears twice`);
    fields[field] = value;
  }
  return normalizeProfile(fields);
}
